import React, { useState, useEffect } from 'react'
import { backendService, FarmSummary, FarmAITokenTransaction } from '../../services/backendService'
import { logInfo, logError } from '../../utils/errorLogger'
import './BalanceManagement.css'

export const FarmAITokens: React.FC = () => {
  const [farms, setFarms] = useState<FarmSummary[]>([])
  const [selectedFarm, setSelectedFarm] = useState<FarmSummary | null>(null)
  const [balance, setBalance] = useState<number | null>(null)
  const [transactions, setTransactions] = useState<FarmAITokenTransaction[]>([])
  const [searchTerm, setSearchTerm] = useState('')

  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [transactionType, setTransactionType] = useState<'credit' | 'debit'>('credit')

  const [isLoadingFarms, setIsLoadingFarms] = useState(false)
  const [isLoadingBalance, setIsLoadingBalance] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  useEffect(() => {
    loadFarms()
  }, [])

  useEffect(() => {
    if (selectedFarm) {
      loadBalance(selectedFarm.id)
    }
  }, [selectedFarm])

  const loadFarms = async () => {
    setIsLoadingFarms(true)
    try {
      const res = await backendService.listAllFarms(1, 200, '', 'name', 'asc', false)
      setFarms(res.farms)
      logInfo(`Loaded ${res.farms.length} farms for AI token management`, 'FarmAITokens')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      logError(`Failed to load farms: ${msg}`, 'FarmAITokens')
    } finally {
      setIsLoadingFarms(false)
    }
  }

  const loadBalance = async (farmId: string) => {
    setIsLoadingBalance(true)
    setBalance(null)
    setTransactions([])
    try {
      const data = await backendService.getFarmAITokenBalance(farmId)
      setBalance(data.balance)
      setTransactions(data.transactions)
      logInfo(`AI token balance for farm ${farmId}: ${data.balance}`, 'FarmAITokens')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      logError(`Failed to load AI token balance: ${msg}`, 'FarmAITokens')
    } finally {
      setIsLoadingBalance(false)
    }
  }

  const handleFarmSelect = (farm: FarmSummary) => {
    setSelectedFarm(farm)
    setAmount('')
    setReason('')
    setSubmitSuccess(false)
    setSubmitError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFarm) return

    const numAmount = parseFloat(amount)
    if (isNaN(numAmount) || numAmount <= 0) {
      setSubmitError('Amount must be a positive number.')
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)
    try {
      if (transactionType === 'credit') {
        await backendService.creditFarmAITokens(selectedFarm.id, numAmount, reason)
      } else {
        await backendService.debitFarmAITokens(selectedFarm.id, numAmount, reason)
      }
      setSubmitSuccess(true)
      setAmount('')
      setReason('')
      await loadBalance(selectedFarm.id)
      logInfo(`AI tokens ${transactionType}ed for farm ${selectedFarm.id}`, 'FarmAITokens')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setSubmitError(`Failed to ${transactionType}: ${msg}`)
      logError(`AI token ${transactionType} failed: ${msg}`, 'FarmAITokens')
    } finally {
      setIsSubmitting(false)
    }
  }

  const filteredFarms = farms.filter(f =>
    f.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.ownerName.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="balance-management" data-testid="farm-ai-tokens">
      <div className="balance-layout">
        {/* Left Panel: Farm Selection */}
        <div className="doctor-selector-panel">
          <h3 className="panel-title">Select Farm</h3>

          <div className="search-box">
            <input
              type="text"
              className="search-input"
              placeholder="Search by business name or owner..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              data-testid="farm-ai-tokens-search"
            />
          </div>

          {isLoadingFarms ? (
            <div className="loading-state">
              <span className="spinner"></span>
              <span>Loading farms...</span>
            </div>
          ) : (
            <div className="doctor-list" data-testid="farm-ai-tokens-farm-list">
              {filteredFarms.length === 0 ? (
                <div className="empty-state">
                  <p>No farms found</p>
                </div>
              ) : (
                filteredFarms.map(farm => (
                  <div
                    key={farm.id}
                    className={`doctor-item ${selectedFarm?.id === farm.id ? 'selected' : ''}`}
                    onClick={() => handleFarmSelect(farm)}
                    data-testid="farm-ai-tokens-farm-item"
                  >
                    <div className="doctor-info">
                      <div className="doctor-name">{farm.businessName}</div>
                      <div className="doctor-username">{farm.city}, {farm.country}</div>
                    </div>
                    <div className="doctor-balance">
                      <span className={`balance-amount ${farm.isActive ? '' : 'inactive'}`}>
                        {farm.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className="balance-label">Status</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Right Panel: AI Token Management */}
        <div className="balance-details-panel">
          {!selectedFarm ? (
            <div className="empty-state-large">
              <div className="empty-icon">🏭</div>
              <p data-testid="farm-ai-tokens-placeholder">Select a farm to manage AI token balance.</p>
            </div>
          ) : (
            <>
              {/* Success/Error Messages */}
              {submitSuccess && (
                <div className="alert alert-success" role="status">
                  <span className="alert-icon">✅</span>
                  <div className="alert-content">
                    <strong>Success!</strong> AI tokens updated successfully.
                  </div>
                </div>
              )}

              {submitError && (
                <div className="alert alert-error" role="alert" data-testid="farm-ai-tokens-error">
                  <span className="alert-icon">❌</span>
                  <div className="alert-content">
                    <strong>Error:</strong> {submitError}
                  </div>
                </div>
              )}

              {/* Current AI Token Balance Card */}
              <div className="balance-card">
                <h3 className="card-title">Farm AI Token Balance</h3>
                <div className="doctor-header">
                  <div className="doctor-name-large">{selectedFarm.businessName}</div>
                  <div className="doctor-meta">
                    {selectedFarm.ownerName} &bull; {selectedFarm.city}, {selectedFarm.country} &bull; {selectedFarm.verificationStatus}
                  </div>
                </div>

                {isLoadingBalance ? (
                  <div className="loading-state">
                    <span className="spinner"></span>
                    <span>Loading AI token balance...</span>
                  </div>
                ) : (
                  <div className="balance-stats">
                    <div className="stat-item stat-primary">
                      <div className="stat-label">AI Token Balance</div>
                      <div className="stat-value" data-testid="farm-ai-tokens-balance">{balance ?? '—'}</div>
                      <div className="stat-unit">AI tokens</div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 16, fontSize: 12, color: '#666', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <span>🆓 Local Ollama: free</span>
                  <span>💡 DeepSeek (cheap): 1 token / word</span>
                  <span>⭐ DeepSeek (premium): 10 tokens / word</span>
                </div>
              </div>

              {/* Credit/Debit AI Tokens Form */}
              <div className="balance-form-card">
                <h3 className="card-title">Manage Farm AI Tokens</h3>
                <form onSubmit={handleSubmit} className="balance-form">
                  <div className="form-row" style={{ marginBottom: 8 }}>
                    <button
                      type="button"
                      className={`toggle-btn ${transactionType === 'credit' ? 'toggle-btn--active' : ''}`}
                      onClick={() => setTransactionType('credit')}
                      data-testid="farm-ai-tokens-credit-btn"
                      disabled={isSubmitting}
                    >
                      Credit
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${transactionType === 'debit' ? 'toggle-btn--active' : ''}`}
                      onClick={() => setTransactionType('debit')}
                      data-testid="farm-ai-tokens-debit-btn"
                      disabled={isSubmitting}
                    >
                      Debit
                    </button>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="farmAiAmount" className="form-label">
                        Amount <span className="required">*</span>
                      </label>
                      <input
                        id="farmAiAmount"
                        type="number"
                        className="form-input"
                        placeholder="100"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        disabled={isSubmitting}
                        min="1"
                        required
                        data-testid="farm-ai-tokens-amount"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="farmAiReason" className="form-label">
                        Reason <span className="required">*</span>
                      </label>
                      <input
                        id="farmAiReason"
                        type="text"
                        className="form-input"
                        placeholder="e.g. Monthly top-up"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={isSubmitting}
                        required
                        data-testid="farm-ai-tokens-reason"
                      />
                    </div>
                  </div>

                  {submitSuccess && <div className="alert alert-success" role="status" data-testid="farm-ai-tokens-success"><span className="alert-icon">✅</span><div className="alert-content">Transaction applied successfully.</div></div>}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                    data-testid="farm-ai-tokens-submit"
                  >
                    {isSubmitting ? 'Processing...' : `Apply ${transactionType === 'credit' ? 'Credit' : 'Debit'}`}
                  </button>
                </form>
              </div>

              {/* Recent Transactions */}
              <div className="balance-card">
                <h3 className="card-title">Recent Transactions</h3>
                {transactions.length === 0 && !isLoadingBalance ? (
                  <p className="empty-state" data-testid="farm-ai-tokens-empty-tx">No transactions yet.</p>
                ) : (
                  <table className="activity-table" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Model</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(tx => (
                        <tr key={tx.id}>
                          <td>{new Date(tx.created_at).toLocaleString()}</td>
                          <td className={tx.amount >= 0 ? 'tx-credit' : 'tx-debit'}>
                            {tx.amount >= 0 ? '+' : ''}{tx.amount}
                          </td>
                          <td>{tx.model_used ?? '—'}</td>
                          <td>{tx.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

